import {
    memo,
    type ReactNode,
    Suspense,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Outlet } from 'react-router-dom';

import { Layout as AntLayout } from 'antd';
import cn from 'classnames';

import { PageLoader } from '../page-loader/page-loader';

import cls from './layout.module.css';

interface LayoutProps {
    headerSlot?: ReactNode;
    sidebarSlot?: ReactNode;
    bottomSlot?: ReactNode;
    flow?: 'auth' | 'app';
    isLoading: boolean;
}

/**
 * App layout
 */
export const Layout = memo((props: LayoutProps) => {
    const {
        headerSlot,
        sidebarSlot,
        bottomSlot,
        flow = 'app',
        isLoading = false,
    } = props;

    const scrollRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const isAuthFlow = useMemo(() => flow === 'auth', [flow]);

    const handleScroll = useCallback(() => {
        if (scrollRef.current) {
            const { scrollTop } = scrollRef.current;

            if (!isScrolled && scrollTop > 0) {
                setIsScrolled(true);
            } else if (scrollTop === 0 && isScrolled) {
                setIsScrolled(false);
            }
        }
    }, [isScrolled]);

    return (
        <AntLayout className={cn(cls.root, { [cls.scrolled]: isScrolled })}>
            {sidebarSlot}
            <AntLayout>
                {headerSlot}
                <AntLayout.Content
                    ref={scrollRef}
                    className={cn(cls.content, {
                        [cls.app]: !isAuthFlow,
                        [cls.auth]: isAuthFlow,
                    })}
                    onScroll={handleScroll}
                >
                    <Suspense fallback={<PageLoader />}>
                        {isLoading ? <PageLoader /> : <Outlet />}
                    </Suspense>
                    {bottomSlot}
                </AntLayout.Content>
            </AntLayout>
        </AntLayout>
    );
});

Layout.displayName = 'Layout';
